import { useEffect, useState } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'https://qihmh0pa24.execute-api.us-west-2.amazonaws.com'

interface Analytics {
  totalLeads: number
  buyerLeads: number
  sellerLeads: number
  hotLeads: number
  scheduledCalls: number
  conversionRate: number
  dailyTrend: Array<{ date: string; leads: number }>
}

interface AnalyticsTabProps {
  clientId: string
}

export function AnalyticsTab({ clientId }: AnalyticsTabProps) {
  const [analytics, setAnalytics] = useState<Analytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchAnalytics()
  }, [clientId])

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/analytics?clientId=${clientId}`)
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch analytics')
      }

      setAnalytics(data.analytics)
    } catch (err: any) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    )
  }

  if (!analytics) {
    return (
      <div className="text-center py-12 text-gray-500">
        No analytics data available
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="text-sm font-medium text-gray-600">Total Leads</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{analytics.totalLeads}</div>
        </div>

        <div className="bg-white border border-blue-200 rounded-lg p-6 shadow-sm">
          <div className="text-sm font-medium text-blue-600">Buyer Leads</div>
          <div className="text-3xl font-bold text-blue-900 mt-2">{analytics.buyerLeads}</div>
        </div>

        <div className="bg-white border border-blue-200 rounded-lg p-6 shadow-sm">
          <div className="text-sm font-medium text-blue-600">Seller Leads</div>
          <div className="text-3xl font-bold text-blue-900 mt-2">{analytics.sellerLeads}</div>
        </div>

        <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
          <div className="text-sm font-medium text-gray-700">Hot Leads</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{analytics.hotLeads}</div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 shadow-sm">
          <div className="text-sm font-medium text-blue-700">Scheduled Calls</div>
          <div className="text-3xl font-bold text-blue-900 mt-2">{analytics.scheduledCalls}</div>
        </div>

        <div className="bg-gray-50 border border-gray-300 rounded-lg p-6 shadow-sm">
          <div className="text-sm font-medium text-gray-700">Conversion Rate</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{analytics.conversionRate.toFixed(1)}%</div>
        </div>
      </div>

      {/* Daily Trend */}
      {analytics.dailyTrend.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Lead Trend</h3>
          <div className="space-y-2">
            {analytics.dailyTrend.slice(-7).map((day) => (
              <div key={day.date} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{day.date}</span>
                <div className="flex items-center gap-2">
                  <div
                    className="h-2 bg-blue-500 rounded"
                    style={{ width: `${Math.max(day.leads * 20, 20)}px` }}
                  ></div>
                  <span className="text-sm font-medium text-gray-900 w-8 text-right">
                    {day.leads}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Data Message */}
      {analytics.totalLeads === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <div className="text-blue-800 font-medium mb-2">No leads yet</div>
          <div className="text-blue-600 text-sm">
            Your analytics will appear here once you start receiving leads through your widget.
          </div>
        </div>
      )}
    </div>
  )
}
